import { useMemo, ReactElement, createElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon, IconRotation } from '@atlassian/learning-components';

import { useQueryParams } from 'src/hooks/useQueryParams';
import { LearningProgressStatus } from 'src/services/http/progress';
import { FilterSectionConfig, SortConfig } from 'src/components/MobileFilterMenu';

import {
  QUERY_PARAM_KEYS,
  DEFAULT_SORT,
  CONTENT_TYPE_VALUES,
  STATUS_LABELS,
  CONTENT_TYPE_LABELS,
  DEFAULT_PRODUCT,
  FILTER_SECTION_TITLES,
} from './constants';

// Sorting options
const SORT_OPTIONS = ['More recent', 'Least recent', 'Most % progress', 'Least % progress'] as const;

// Filter options
const STATUS_OPTIONS = [
  STATUS_LABELS.NOT_STARTED,
  STATUS_LABELS.IN_PROGRESS,
  STATUS_LABELS.COMPLETE,
  STATUS_LABELS.SAVED,
  STATUS_LABELS.RETIRED,
] as const;
const TYPE_OPTIONS = [
  CONTENT_TYPE_LABELS.COURSE,
  CONTENT_TYPE_LABELS.LEARNING_PATH,
  CONTENT_TYPE_LABELS.LESSON,
] as const;
const APPS_OPTIONS = [
  'Trello',
  'Jira Product Discovery',
  'Confluence',
  'Bitbucket',
  'Loom',
  'Jira Service Management',
  'Compass',
  'Forge',
  'Jira Align',
  'Guard',
  'Jira',
  'Rovo',
] as const;

// Duration filter constants
const DURATION_FILTERS = {
  LESS_THAN_30_MIN: {
    label: 'Less than 30 min',
    matches: (duration: number) => duration < 30,
  },
  THIRTY_MIN_TO_ONE_HOUR: {
    label: '30 min to 1 hour',
    matches: (duration: number) => duration >= 30 && duration <= 60,
  },
  ONE_TO_TWO_HOURS: {
    label: '1 to 2 hours',
    matches: (duration: number) => duration > 60 && duration <= 120,
  },
  MORE_THAN_TWO_HOURS: {
    label: 'More than 2 hours',
    matches: (duration: number) => duration > 120,
  },
} as const;

const DURATION_OPTIONS = Object.values(DURATION_FILTERS).map((filter) => filter.label) as readonly string[];

interface FilterItem {
  isSelected: boolean;
  isDisabled: boolean;
  icon?: ReactElement;
}

// Type for flattened items
type FlattenedItem = {
  contentType: 'lesson' | 'course' | 'learningPath';
  duration?: number;
  product?: string;
  status?: LearningProgressStatus;
  isSaved?: boolean;
  isRetired?: boolean;
};

interface UseMyLearningFiltersReturn {
  // Selected values
  selectedStatus: string[];
  selectedType: string[];
  selectedApps: string[];
  selectedDuration: string[];
  currentSort: string;
  selectedFiltersLength: number;
  hasActiveFilters: boolean;

  // Filter items for dropdowns
  statusItems: Record<string, FilterItem>;
  typeItems: Record<string, FilterItem>;
  appsItems: Record<string, FilterItem>;
  durationItems: Record<string, FilterItem>;
  sortItems: Record<string, FilterItem>;

  // Handlers
  handleFilterChange: (key: string, value: string, isCurrentlySelected: boolean) => void;
  handleSortChange: (label: string) => void;
  clearAllFilters: () => void;

  // Mobile config
  mobileSortConfig: SortConfig;
  mobileFilterSections: FilterSectionConfig[];
}

export const useMyLearningFilters = (allItems: FlattenedItem[]): UseMyLearningFiltersReturn => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setQueryParam, removeQueryParam, clearAllQueryParams } = useQueryParams();

  // Get current sort value from URL
  const currentSort = searchParams.get(QUERY_PARAM_KEYS.SORT) || DEFAULT_SORT;

  // Get current filter values from URL
  const getSelectedFilters = (key: string): string[] => {
    const param = searchParams.get(key);

    return param ? param.split(',') : [];
  };

  const selectedStatus = getSelectedFilters(QUERY_PARAM_KEYS.STATUS);
  const selectedType = getSelectedFilters(QUERY_PARAM_KEYS.TYPE);
  const selectedApps = getSelectedFilters(QUERY_PARAM_KEYS.APPS);
  const selectedDuration = getSelectedFilters(QUERY_PARAM_KEYS.DURATION);

  // Calculate total number of selected filters for mobile filter badge
  const selectedFiltersLength =
    selectedStatus.length + selectedType.length + selectedApps.length + selectedDuration.length;

  // Check if any filters are active
  const hasActiveFilters =
    selectedStatus.length > 0 || selectedType.length > 0 || selectedApps.length > 0 || selectedDuration.length > 0;

  // Create filter items for sort dropdown (single select)
  const sortItems: Record<string, FilterItem> = SORT_OPTIONS.reduce(
    (acc, option) => {
      // Determine icon based on sort option
      let icon: ReactElement | undefined;

      if (option.includes('Most') || option.includes('More')) {
        icon = createElement(Icon, { type: 'arrow-up' });
      } else if (option.includes('Least')) {
        icon = createElement(Icon, { type: 'arrow-up', rotation: IconRotation.ONE_EIGHTY as IconRotation });
      }

      return {
        ...acc,
        [option]: {
          isSelected: currentSort === option,
          isDisabled: false,
          icon,
        },
      };
    },
    {} as Record<string, FilterItem>,
  );

  // Create filter items for multi-select dropdowns
  const createFilterItems = (options: readonly string[], selected: string[]): Record<string, FilterItem> => {
    return options.reduce(
      (acc, option) => ({
        ...acc,
        [option]: {
          isSelected: selected.includes(option),
          isDisabled: false,
        },
      }),
      {} as Record<string, FilterItem>,
    );
  };

  const statusItems = createFilterItems(STATUS_OPTIONS, selectedStatus);
  const typeItems = createFilterItems(TYPE_OPTIONS, selectedType);
  const appsItems = createFilterItems(APPS_OPTIONS, selectedApps);
  const durationItems = createFilterItems(DURATION_OPTIONS, selectedDuration);

  // Handle sort change (single select - replace existing)
  const handleSortChange = (label: string): void => {
    const newSearchParams = new URLSearchParams(searchParams);

    newSearchParams.set(QUERY_PARAM_KEYS.SORT, label);
    navigate({ search: newSearchParams.toString() });
  };

  // Handle filter change (multi-select - toggle)
  const handleFilterChange = (key: string, value: string, isCurrentlySelected: boolean): void => {
    if (isCurrentlySelected) {
      removeQueryParam(key, value);
    } else {
      setQueryParam(key, value);
    }
  };

  // Calculate counts for each filter option
  const filterCounts = useMemo(() => {
    const counts = {
      status: {} as Record<string, number>,
      type: {} as Record<string, number>,
      apps: {} as Record<string, number>,
      duration: {} as Record<string, number>,
    };

    allItems.forEach((item) => {
      // Count by status
      if (item.status === LearningProgressStatus.NOT_STARTED) {
        counts.status[STATUS_LABELS.NOT_STARTED] = (counts.status[STATUS_LABELS.NOT_STARTED] || 0) + 1;
      } else if (item.status === LearningProgressStatus.IN_PROGRESS) {
        counts.status[STATUS_LABELS.IN_PROGRESS] = (counts.status[STATUS_LABELS.IN_PROGRESS] || 0) + 1;
      } else if (item.status === LearningProgressStatus.COMPLETED) {
        counts.status[STATUS_LABELS.COMPLETE] = (counts.status[STATUS_LABELS.COMPLETE] || 0) + 1;
      }
      if (item.isSaved) {
        counts.status[STATUS_LABELS.SAVED] = (counts.status[STATUS_LABELS.SAVED] || 0) + 1;
      }
      if (item.isRetired) {
        counts.status[STATUS_LABELS.RETIRED] = (counts.status[STATUS_LABELS.RETIRED] || 0) + 1;
      }

      // Count by type
      if (item.contentType === CONTENT_TYPE_VALUES.LESSON) {
        counts.type[CONTENT_TYPE_LABELS.LESSON] = (counts.type[CONTENT_TYPE_LABELS.LESSON] || 0) + 1;
      } else if (item.contentType === CONTENT_TYPE_VALUES.COURSE) {
        counts.type[CONTENT_TYPE_LABELS.COURSE] = (counts.type[CONTENT_TYPE_LABELS.COURSE] || 0) + 1;
      } else if (item.contentType === CONTENT_TYPE_VALUES.LEARNING_PATH) {
        counts.type[CONTENT_TYPE_LABELS.LEARNING_PATH] = (counts.type[CONTENT_TYPE_LABELS.LEARNING_PATH] || 0) + 1;
      }

      // Count by app/product
      const product = item.product || DEFAULT_PRODUCT;

      APPS_OPTIONS.forEach((app) => {
        if (product.toLowerCase().includes(app.toLowerCase())) {
          counts.apps[app] = (counts.apps[app] || 0) + 1;
        }
      });

      // Count by duration
      const duration = item.duration || 0;

      Object.values(DURATION_FILTERS).forEach((filter) => {
        if (filter.matches(duration)) {
          counts.duration[filter.label] = (counts.duration[filter.label] || 0) + 1;
        }
      });
    });

    return counts;
  }, [allItems]);

  // Mobile filter configuration with counts
  const mobileFilterSections: FilterSectionConfig[] = useMemo(
    () => [
      {
        key: QUERY_PARAM_KEYS.STATUS,
        title: FILTER_SECTION_TITLES.STATUS,
        options: STATUS_OPTIONS.map((status) => ({
          label: status,
          count: filterCounts.status[status] || 0,
        })),
      },
      {
        key: QUERY_PARAM_KEYS.TYPE,
        title: FILTER_SECTION_TITLES.TYPE,
        options: TYPE_OPTIONS.map((type) => ({
          label: type,
          count: filterCounts.type[type] || 0,
        })),
      },
      {
        key: QUERY_PARAM_KEYS.APPS,
        title: FILTER_SECTION_TITLES.APP,
        options: APPS_OPTIONS.map((app) => ({
          label: app,
          count: filterCounts.apps[app] || 0,
        })),
      },
      {
        key: QUERY_PARAM_KEYS.DURATION,
        title: FILTER_SECTION_TITLES.DURATION,
        options: DURATION_OPTIONS.map((duration) => ({
          label: duration,
          count: filterCounts.duration[duration] || 0,
        })),
      },
    ],
    [filterCounts],
  );

  // Mobile sort configuration
  const mobileSortConfig: SortConfig = {
    items: sortItems,
    currentSort: currentSort,
  };

  return {
    // Selected values
    selectedStatus,
    selectedType,
    selectedApps,
    selectedDuration,
    currentSort,
    selectedFiltersLength,
    hasActiveFilters,

    // Filter items
    statusItems,
    typeItems,
    appsItems,
    durationItems,
    sortItems,

    // Handlers
    handleFilterChange,
    handleSortChange,
    clearAllFilters: clearAllQueryParams,

    // Mobile config
    mobileSortConfig,
    mobileFilterSections,
  };
};
